import { doc, collection, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/firebase'
import { RoleUtils } from '@/composables/roles/roleConfig'
import { RULE_EXPECTATIONS, attempt, accessDetail, fmtExpected } from '../ruleExpectations.js'

// Other plan: accommodations for staff, no calendar meetings, feedback only
// for the 504 coordinator who case-manages the student.
//
// Rules side: meeting create must be denied even when this user is the case
// manager. Feedback-instance create stays on the existing admin/CM rule
// (broader than the button). Client side: the Feedback button is on only for
// admin_504 on their own Other caseload.
//
// Run as admin_504 for the full picture. school_admin and admin can create
// the student and must also be denied a meeting; their Feedback button stays
// off. Roles that cannot create students skip.
export default {
  id: 'otherPlan',
  label: 'Other plan (no meetings, coordinator feedback)',
  description:
    'Other-plan student you case-manage. Meeting writes denied. Feedback button on only for admin_504.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    if (!RULE_EXPECTATIONS.studentCreate(role)) {
      step.skip(
        'Other plan',
        `${role} cannot create students. Run this as admin_504 (the real owner), or as school_admin / admin.`
      )
      return
    }
    if (Array.isArray(ctx.tokenSchoolIds) && !ctx.schoolId) {
      step.block('Other plan', 'Select a school before creating a school-scoped student')
      return
    }

    const studentRef = doc(collection(db, 'students'))
    const meetingRef = doc(collection(db, 'meetings'))
    const feedbackRef = doc(collection(db, 'feedbackInstances'))
    const note = `SMOKE SST ${ctx.runId.slice(-6)}`
    const tag = { __smokeTest: true, __smokeRunId: ctx.runId }
    const school = ctx.schoolId ? { schoolId: ctx.schoolId } : {}
    let created = false

    try {
      await step('create Other-plan student (expected: allowed)', async () => {
        const { allowed } = await attempt(() =>
          setDoc(studentRef, {
            app: {
              studentData: {
                firstName: 'SMOKE',
                lastName: `Other ${ctx.runId.slice(-8)}`,
                plan: 'Other',
                planNote: note,
                planStatus: '',
                grade: '7',
                ssid: `SMOKE-OTHER-${ctx.runId}`,
                caseManagerId: ctx.user.uid
              },
              accommodations: { instruction: 'extra time', assessment: '' },
              schedule: { classServices: [] },
              staffIds: []
            },
            ...tag,
            createdAt: serverTimestamp(),
            ...school
          })
        )
        created = allowed
        return allowed
          ? `${accessDetail(true, allowed)} — students/${studentRef.id}`
          : accessDetail(true, allowed)
      })

      if (!created) return

      await step('read back plan and note', async () => {
        const snap = await getDoc(studentRef)
        if (!snap.exists()) throw new Error('Created student not found on read-back')
        const sd = snap.data()?.app?.studentData || {}
        if (sd.plan !== 'Other') throw new Error(`plan is "${sd.plan}", expected Other`)
        if (sd.planNote !== note) throw new Error(`planNote is "${sd.planNote}"`)
        return 'plan Other and planNote round-tripped'
      })

      const expectMeeting = RULE_EXPECTATIONS.meetingWrite(role, {
        studentExists: true,
        uid: ctx.user.uid,
        caseManagerId: ctx.user.uid,
        plan: 'Other'
      })
      await step(
        `write meeting for Other plan (expected: ${fmtExpected(expectMeeting)})`,
        async () => {
          const { allowed } = await attempt(() =>
            setDoc(meetingRef, {
              studentId: studentRef.id,
              date: '2026-10-15',
              type: 'Other',
              status: 'hold',
              ...tag,
              createdAt: serverTimestamp(),
              ...school
            })
          )
          return accessDetail(expectMeeting, allowed)
        }
      )

      const expectFeedbackButton = role === 'admin_504'
      await step(
        `client feedback button (expected: ${expectFeedbackButton ? 'shown' : 'hidden'})`,
        async () => {
          const shown = RoleUtils.canSendFeedback(
            ctx.user.uid,
            role,
            { app: { studentData: { plan: 'Other', caseManagerId: ctx.user.uid } } },
            null
          )
          if (shown !== expectFeedbackButton) {
            throw new Error(
              `canSendFeedback returned ${shown}; expected ${expectFeedbackButton} for ${role}`
            )
          }
          return expectFeedbackButton
            ? '504 coordinator can send feedback for this Other student'
            : 'feedback button stays hidden for this role'
        }
      )

      const expectFeedback = RULE_EXPECTATIONS.feedbackInstanceCreate(role)
      await step(
        `create feedback instance (expected: ${fmtExpected(expectFeedback)})`,
        async () => {
          const { allowed } = await attempt(() =>
            setDoc(feedbackRef, {
              studentId: studentRef.id,
              caseManagerId: ctx.user.uid,
              assignedTeachers: [],
              status: 'active',
              audienceType: 'student',
              templateTitle: 'SMOKE Other plan',
              studentName: 'SMOKE Other',
              ...tag,
              createdAt: serverTimestamp(),
              ...school
            })
          )
          return accessDetail(expectFeedback, allowed)
        }
      )
    } finally {
      await deleteDoc(meetingRef).catch(() => {})
      await deleteDoc(feedbackRef).catch(() => {})
      await deleteDoc(studentRef).catch(() => {})
    }
  }
}
